import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE } from "../../utils/api";
import { formatPrice } from "../../utils/format";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { KakaoMapView } from "../../components/KakaoMapView";

// ── 인터페이스
interface PreviewListing {
  id: string;
  property_type: string;
  region_sido: string;
  region_sigungu: string;
  region_eupmyeondong: string;
  area_pyeong: number;
  price_manwon: number;
  land_category: string;
  broker_is_verified?: boolean;
  blind_lat?: number;
  blind_lng?: number;
}
interface PublicStats {
  listings: number;
  brokers: number;
  inquiries: number;
  ndas: number;
}

// ── 훅
function useFadeIn<T extends HTMLElement>(threshold = 0.12) {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

function useCountUp(target: number, duration = 1600, active = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active || target === 0) return;
    let startTs: number | null = null;
    const tick = (ts: number) => {
      if (!startTs) startTs = ts;
      const p = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(eased * target));
      if (p < 1) requestAnimationFrame(tick);
      else setCount(target);
    };
    requestAnimationFrame(tick);
  }, [target, duration, active]);
  return count;
}

// ── 상수
const TYPE_COLORS: Record<string, string> = {
  "\uB18D\uC9C0": "#22C55E", "\uC784\uC57C": "#A16207", "\uC0C1\uAC00": "#6366F1",
  "\uC8FC\uD0DD": "#3B82F6", "\uBB18\uD130": "#A855F7", "\uACF5\uC7A5": "#6B7280", "\uCC3D\uACE0": "#F97316",
};

const ROADMAP_ITEMS = [
  { icon: "\uD83C\uDFE1", title: "\uB545\uC8FC\uC778 \uC9C1\uC811 \uB4F1\uB85D", open: true },
  { icon: "\uD83D\uDC64", title: "\uC911\uAC1C\uC0AC \uC804\uBB38\uAC00 \uD504\uB85C\uD544", open: true },
  { icon: "\u26B0\uFE0F", title: "\uBB18\uD130 \uC804\uC6A9 \uAC80\uC0C9", open: true },
  { icon: "\uD83E\uDD16", title: "AI \uB9E4\uBB3C \uBD84\uC11D \uB9AC\uD3EC\uD2B8", open: true },
];

// ────────────────────────────────────────────────────────────
export function LandingPage() {
  const navigate = useNavigate();
  const m = useIsMobile();
  usePageTitle();

  // ── 모달
  const [showLandModal, setShowLandModal] = useState(false);
  const [landAlertSent, setLandAlertSent] = useState(false);

  // ── 매물 데이터
  const [previews, setPreviews] = useState<PreviewListing[]>([]);
  const [totalListings, setTotalListings] = useState(350);
  const [allCoords, setAllCoords] = useState<{ lat: number; lng: number }[]>([]);

  useEffect(() => {
    fetch(`${API_BASE}/api/listings?limit=500`)
      .then(r => r.json())
      .then(d => {
        const all: PreviewListing[] = d.listings || [];
        if (all.length > 0) setTotalListings(all.length);

        const coords = all
          .filter(l => l.blind_lat && l.blind_lng)
          .map(l => ({ lat: l.blind_lat!, lng: l.blind_lng! }));
        if (coords.length > 0) setAllCoords(coords);

        if (all.length <= 6) { setPreviews(all); return; }
        const byType: Record<string, PreviewListing[]> = {};
        for (const l of all) {
          const t = l.property_type || "\uAE30\uD0C0";
          if (!byType[t]) byType[t] = [];
          byType[t].push(l);
        }
        const types = Object.keys(byType);
        const picked: PreviewListing[] = [];
        let idx = 0;
        while (picked.length < 6 && idx < all.length) {
          for (const t of types) {
            if (picked.length >= 6) break;
            const item = byType[t][Math.floor(idx / types.length)];
            if (item && !picked.includes(item)) picked.push(item);
          }
          idx += types.length;
        }
        setPreviews(picked);
      })
      .catch((e) => console.warn('listings preview fetch failed:', e));
  }, []);

  // ── 공개 통계
  const [stats, setStats] = useState<PublicStats | null>(null);
  useEffect(() => {
    fetch(`${API_BASE}/api/stats/public`)
      .then(r => r.json())
      .then(d => setStats(d as PublicStats))
      .catch((e) => console.warn('public stats fetch failed:', e));
  }, []);

  // ── 섹션 페이드인
  const brokerTargetSec = useFadeIn<HTMLElement>();
  const buyerTargetSec  = useFadeIn<HTMLElement>();
  const landTargetSec   = useFadeIn<HTMLElement>();
  const problemSec      = useFadeIn<HTMLElement>();
  const listingsSec     = useFadeIn<HTMLElement>();
  const statsSec        = useFadeIn<HTMLElement>();
  const roadmapSec      = useFadeIn<HTMLElement>();
  const ctaSec          = useFadeIn<HTMLElement>();

  const [previewVisible, setPreviewVisible] = useState(false);
  useEffect(() => {
    if (previews.length > 0) {
      const t = setTimeout(() => setPreviewVisible(true), 50);
      return () => clearTimeout(t);
    }
  }, [previews.length]);

  // ── 카운트업
  const cntListings  = useCountUp(stats?.listings  ?? totalListings, 1600, statsSec.visible);
  const cntBrokers   = useCountUp(stats?.brokers   ?? 38,            1600, statsSec.visible);
  const cntInquiries = useCountUp(stats?.inquiries ?? 120,           1600, statsSec.visible);
  const cntNda       = useCountUp(stats?.ndas      ?? 95,            1600, statsSec.visible);

  const fadeIn = (visible: boolean): React.CSSProperties => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(28px)",
    transition: "opacity 0.7s ease, transform 0.7s ease",
  });

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", color: "var(--text-primary)" }}>

      <style>{`
        @keyframes heroBadge {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .lt-btn-gold { transition: all 0.22s !important; }
        .lt-btn-gold:hover { transform: translateY(-3px) !important; box-shadow: 0 10px 30px rgba(212,168,83,0.5) !important; }
        .lt-btn-ghost:hover { border-color: #D4A853 !important; color: #D4A853 !important; background: rgba(212,168,83,0.07) !important; }
        .lt-card { transition: all 0.22s !important; }
        .lt-card:hover { transform: translateY(-4px) !important; border-color: rgba(212,168,83,0.35) !important; }
        .lt-preview:hover { transform: translateY(-3px) !important; border-color: rgba(212,168,83,0.4) !important; box-shadow: 0 6px 24px rgba(0,0,0,0.3) !important; }
        .lt-roadmap-card { transition: all 0.22s !important; }
        .lt-roadmap-card:hover { transform: translateY(-4px) !important; border-color: rgba(212,168,83,0.3) !important; }
      `}</style>

      <AppHeader />

      {/* ══════════════════════════════════════
          히어로 — 새 헤드라인 + 카카오맵
      ══════════════════════════════════════ */}
      <section style={{
        background: "linear-gradient(180deg, #040C18 0%, #070F1C 60%, #0A1320 100%)",
        padding: m ? "32px 20px 0" : "36px 24px 0",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* 배경 등고선 */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          opacity: 0.04,
          background: [
            "repeating-radial-gradient(circle at 30% 40%, transparent 0, transparent 52px, rgba(212,168,83,0.6) 53px, transparent 54px)",
            "repeating-radial-gradient(circle at 70% 20%, transparent 0, transparent 70px, rgba(212,168,83,0.4) 71px, transparent 72px)",
          ].join(", "),
        }} />

        {/* 텍스트 중앙 */}
        <div style={{
          maxWidth: 680, margin: "0 auto",
          textAlign: "center",
          position: "relative", zIndex: 1,
          paddingBottom: m ? 16 : 20,
        }}>
          {/* 브랜드 슬로건 */}
          <div style={{ marginBottom: m ? 14 : 18 }}>
            <span style={{
              display: "block",
              fontSize: m ? 18 : 22, fontWeight: 600,
              color: "#D4A853", letterSpacing: "0.1em",
            }}>다가치</span>
            <span style={{
              display: "block",
              fontSize: m ? 12 : 13, fontWeight: 400,
              color: "rgba(212,168,83,0.7)", letterSpacing: "0.04em",
              marginTop: 5,
            }}>다같이 · 다양한 가치</span>
          </div>

          {/* 배지 */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            padding: "5px 18px", borderRadius: 100,
            border: "1px solid rgba(212,168,83,0.28)",
            background: "rgba(212,168,83,0.07)",
            color: "#D4A853", fontSize: 10, fontWeight: 700,
            letterSpacing: 2.5, textTransform: "uppercase" as const,
            marginBottom: m ? 10 : 14,
            animation: "heroBadge 0.5s ease both",
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: "#D4A853", display: "inline-block",
              boxShadow: "0 0 5px rgba(212,168,83,0.8)",
            }} />
            BLIND MLS
          </div>

          {/* H1 */}
          <h1 style={{
            fontSize: m ? "clamp(26px, 7vw, 34px)" : "clamp(32px, 3.5vw, 44px)",
            fontWeight: 800, lineHeight: 1.15,
            color: "#F8FAFC", margin: "0 0 10px",
            letterSpacing: "-0.03em",
          }}>
            토지 거래의
            <br />
            <span style={{
              background: "linear-gradient(135deg, #C49A3C 0%, #D4A853 45%, #E8C77A 70%, #D4A853 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>
              새로운 기준
            </span>
          </h1>

          {/* 구분선 */}
          <div style={{
            width: 52, height: 2,
            margin: "0 auto",
            marginBottom: m ? 10 : 12,
            background: "linear-gradient(90deg, transparent, #D4A853, transparent)",
          }} />

          {/* 서브 카피 */}
          <p style={{
            fontSize: m ? 14 : 16,
            color: "#94A3B8", lineHeight: 1.7,
            margin: "0 auto",
            marginBottom: m ? 16 : 20,
            maxWidth: 520,
          }}>
            검증된 중개사만, 검증된 매물만, NDA로 보호된 거래
          </p>

          {/* CTA 버튼 */}
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" as const }}>
            <button
              className="lt-btn-gold"
              style={{
                padding: m ? "11px 22px" : "13px 32px",
                borderRadius: 10, border: "none",
                background: "linear-gradient(135deg, #D4A853 0%, #C49A3C 100%)",
                color: "#080E1A", fontSize: m ? 13 : 14, fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 4px 18px rgba(212,168,83,0.32)",
              }}
              onClick={() => navigate("/signup")}
            >
              중개사 회원가입
            </button>
            <button
              className="lt-btn-ghost"
              style={{
                padding: m ? "11px 22px" : "13px 32px",
                borderRadius: 10,
                border: "1.5px solid rgba(148,163,184,0.28)",
                background: "transparent",
                color: "#6B7F94", fontSize: m ? 13 : 14, fontWeight: 600,
                cursor: "pointer", transition: "all 0.22s",
              }}
              onClick={() => navigate("/listings")}
            >
              매물 찾아보기
            </button>
          </div>
        </div>

        {/* 카카오맵 */}
        <div style={{ position: "relative", width: "100%" }}>
          <KakaoMapView
            active={allCoords.length > 0}
            updateKey={String(allCoords.length)}
            height={m ? 300 : 400}
            coords={allCoords}
            interactive={false}
            onAreaClick={() => navigate("/listings")}
            mapStyle={{ display: "block" }}
            filterStyle="brightness(0.5) contrast(1.2) saturate(0) hue-rotate(180deg)"
            centerLat={36.2}
            centerLng={127.8}
            mapLevel={12}
          />

          {allCoords.length > 0 && (<>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 60, background: "linear-gradient(to bottom, #070F1C 0%, transparent 100%)", pointerEvents: "none", zIndex: 5 }} />
            <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: m ? 48 : 80, background: "linear-gradient(to right, #070F1C 0%, transparent 100%)", pointerEvents: "none", zIndex: 5 }} />
            <div style={{ position: "absolute", top: 0, bottom: 0, right: 0, width: 120, background: "linear-gradient(to left, #070F1C 0%, transparent 100%)", pointerEvents: "none", zIndex: 5 }} />
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              padding: m ? "40px 20px 16px" : "60px 20px 20px",
              background: "linear-gradient(to top, rgba(4,12,24,0.97) 0%, rgba(4,12,24,0.5) 55%, transparent 100%)",
              textAlign: "center", pointerEvents: "none", zIndex: 5,
            }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: m ? "6px 16px" : "7px 20px",
                borderRadius: 100, background: "rgba(0,0,0,0.55)",
                border: "1px solid rgba(255,255,255,0.1)", marginBottom: 8,
              }}>
                <span style={{ fontSize: m ? 15 : 17, fontWeight: 800, color: "#34D399", letterSpacing: "-0.01em" }}>
                  {totalListings}+
                </span>
                <span style={{ fontSize: m ? 13 : 14, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                  비공개 매물이 등록되어 있습니다
                </span>
              </div>
              <p style={{ fontSize: 10, color: "#64748B", margin: 0 }}>
                블라인드 좌표 표시 — 정확한 위치는 NDA 서명 후 공개
              </p>
            </div>
          </>)}
        </div>
      </section>

      {/* ══════════════════════════════════════
          타깃 섹션 1: 중개사라면
      ══════════════════════════════════════ */}
      <section
        ref={brokerTargetSec.ref}
        style={{
          padding: m ? "48px 16px" : "80px 48px",
          background: "#07101E",
          ...fadeIn(brokerTargetSec.visible),
        }}
      >
        <div style={{ maxWidth: 940, margin: "0 auto" }}>
          <div style={{
            display: "flex",
            flexDirection: m ? "column" : "row",
            gap: m ? 40 : 60,
            alignItems: "center",
          }}>
            <div style={{ width: m ? "100%" : "50%", fontSize: m ? 80 : 128, lineHeight: 1, textAlign: "center", flexShrink: 0 }}>
              {"\uD83C\uDFE2"}
            </div>
            <div style={{ width: m ? "100%" : "50%", maxWidth: 512 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 2.5,
                textTransform: "uppercase" as const, color: "#D4A853",
                display: "block", marginBottom: 12,
              }}>FOR BROKERS</span>
              <h2 style={{
                fontSize: m ? 22 : 30, fontWeight: 800,
                color: "#F8FAFC", margin: "0 0 14px", letterSpacing: "-0.02em",
                lineHeight: 1.25,
              }}>
                오프마켓 매물에 가장 먼저 접근하세요
              </h2>
              <p style={{
                fontSize: m ? 14 : 16, color: "#94A3B8",
                lineHeight: 1.65, margin: "0 0 28px", maxWidth: 380,
              }}>
                자격증 인증 후 350건 이상의 비공개 토지 매물을 즉시 열람.
                검증된 파트너만 모인 프라이빗 네트워크.
              </p>
              <button
                className="lt-btn-gold"
                style={{
                  padding: m ? "12px 28px" : "14px 38px",
                  borderRadius: 10, border: "none",
                  background: "linear-gradient(135deg, #D4A853, #C49A3C)",
                  color: "#080E1A", fontSize: m ? 14 : 15, fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 4px 18px rgba(212,168,83,0.32)",
                }}
                onClick={() => navigate("/signup")}
              >
                중개사 회원가입 {"\u2192"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          타깃 섹션 2: 구매자라면
      ══════════════════════════════════════ */}
      <section
        ref={buyerTargetSec.ref}
        style={{
          padding: m ? "48px 16px" : "80px 48px",
          background: "var(--bg-primary)",
          ...fadeIn(buyerTargetSec.visible),
        }}
      >
        <div style={{ maxWidth: 940, margin: "0 auto" }}>
          <div style={{
            display: "flex",
            flexDirection: m ? "column" : "row",
            gap: m ? 40 : 60,
            alignItems: "center",
          }}>
            {/* 텍스트 — 왼쪽 (모바일: 상단) */}
            <div style={{ width: m ? "100%" : "50%", maxWidth: 512 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 2.5,
                textTransform: "uppercase" as const, color: "#D4A853",
                display: "block", marginBottom: 12,
              }}>FOR BUYERS</span>
              <h2 style={{
                fontSize: m ? 22 : 30, fontWeight: 800,
                color: "#F8FAFC", margin: "0 0 14px", letterSpacing: "-0.02em",
                lineHeight: 1.25,
              }}>
                묘터부터 농지까지, 공개되지 않은 토지를 먼저
              </h2>
              <p style={{
                fontSize: m ? 14 : 16, color: "#94A3B8",
                lineHeight: 1.65, margin: "0 0 28px", maxWidth: 380,
              }}>
                일반 부동산 플랫폼에 나오지 않는 매물.
                NDA 서명 한 번으로 실제 위치·가격 즉시 열람.
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" as const }}>
                <button
                  className="lt-btn-ghost"
                  style={{
                    padding: m ? "12px 28px" : "14px 38px",
                    borderRadius: 10,
                    border: "1.5px solid rgba(212,168,83,0.4)",
                    background: "rgba(212,168,83,0.06)",
                    color: "#D4A853", fontSize: m ? 14 : 15, fontWeight: 600,
                    cursor: "pointer", transition: "all 0.22s",
                  }}
                  onClick={() => navigate("/listings")}
                >
                  매물 둘러보기 {"\u2192"}
                </button>
                <button
                  style={{
                    padding: m ? "12px 20px" : "14px 28px",
                    borderRadius: 10,
                    border: "1.5px solid rgba(124,58,237,0.4)",
                    background: "rgba(124,58,237,0.08)",
                    color: "#A78BFA", fontSize: m ? 13 : 14, fontWeight: 600,
                    cursor: "pointer", transition: "all 0.22s",
                  }}
                  onClick={() => navigate("/burial")}
                >
                  {"\u26B0\uFE0F"} 묘터 전용 검색
                </button>
              </div>
            </div>
            {/* 아이콘 — 오른쪽 (모바일: 하단) */}
            <div style={{ width: m ? "100%" : "50%", fontSize: m ? 80 : 128, lineHeight: 1, textAlign: "center", flexShrink: 0 }}>
              {"\uD83D\uDD0D"}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          타깃 섹션 3: 땅주인이라면
      ══════════════════════════════════════ */}
      <section
        ref={landTargetSec.ref}
        style={{
          padding: m ? "48px 16px" : "80px 48px",
          background: "#07101E",
          ...fadeIn(landTargetSec.visible),
        }}
      >
        <div style={{ maxWidth: 940, margin: "0 auto" }}>
          <div style={{
            display: "flex",
            flexDirection: m ? "column" : "row",
            gap: m ? 40 : 60,
            alignItems: "center",
          }}>
            <div style={{ width: m ? "100%" : "50%", fontSize: m ? 80 : 128, lineHeight: 1, textAlign: "center", flexShrink: 0 }}>
              {"\uD83C\uDF31"}
            </div>
            <div style={{ width: m ? "100%" : "50%", maxWidth: 512 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 2.5,
                textTransform: "uppercase" as const, color: "#D4A853",
                display: "block", marginBottom: 12,
              }}>FOR LANDOWNERS</span>
              <h2 style={{
                fontSize: m ? 22 : 30, fontWeight: 800,
                color: "#F8FAFC", margin: "0 0 14px", letterSpacing: "-0.02em",
                lineHeight: 1.25,
              }}>
                내 땅, 조용히 팔 수 있어요
              </h2>
              <p style={{
                fontSize: m ? 14 : 16, color: "#94A3B8",
                lineHeight: 1.65, margin: "0 0 28px", maxWidth: 380,
              }}>
                공개 없이 검증된 중개사에게만 연결됩니다.
                등록 무료, 원하는 조건의 중개사만 선택하세요.
              </p>
              <button
                style={{
                  padding: m ? "12px 28px" : "14px 38px",
                  borderRadius: 10,
                  border: "none",
                  background: "linear-gradient(135deg, #B8972E, #D4AF37)",
                  color: "#0F172A", fontSize: m ? 14 : 15, fontWeight: 700,
                  cursor: "pointer", transition: "all 0.22s",
                }}
                onClick={() => navigate("/owner/register")}
              >
                내 땅 등록하기 {"\u2192"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          왜 주소를 숨기는가?
      ══════════════════════════════════════ */}
      <section
        ref={problemSec.ref}
        style={{
          padding: m ? "64px 20px" : "80px 24px",
          background: "var(--bg-primary)",
          ...fadeIn(problemSec.visible),
        }}
      >
        <div style={{ maxWidth: 940, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: m ? 40 : 56 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 2.5,
              textTransform: "uppercase" as const, color: "#D4A853", display: "block", marginBottom: 12,
            }}>BLIND MLS</span>
            <h2 style={{
              fontSize: m ? 24 : 36, fontWeight: 800,
              color: "#F8FAFC", margin: "0 0 12px", letterSpacing: "-0.02em",
            }}>왜 주소를 숨기는가?</h2>
            <p style={{ fontSize: m ? 15 : 17, color: "#94A3B8", maxWidth: 420, margin: "0 auto", lineHeight: 1.7 }}>
              좋은 매물을 올리면 빼앗기는 구조,<br />다가치 땅신뢰가 근본적으로 해결합니다.
            </p>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: m ? "1fr" : "1fr 1fr",
            gap: m ? 16 : 24,
          }}>
            {/* 기존 방식 */}
            <div style={{
              background: "rgba(239,68,68,0.03)",
              border: "1px solid rgba(239,68,68,0.15)",
              borderRadius: 20, padding: m ? "26px 22px" : "36px 32px",
              position: "relative" as const,
            }}>
              <div style={{
                position: "absolute" as const, top: 0, left: 0, right: 0, height: 3,
                borderRadius: "20px 20px 0 0",
                background: "linear-gradient(90deg, #EF4444, #B91C1C)",
              }} />
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "4px 12px", borderRadius: 100,
                background: "rgba(239,68,68,0.1)", color: "#F87171",
                fontSize: 10, fontWeight: 700, letterSpacing: 2,
                textTransform: "uppercase" as const, marginBottom: 22,
              }}>
                {"⚠️"} 기존 방식
              </span>
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
                {[
                  "힘들게 발굴한 매물을 네이버 부동산에 등록",
                  "경쟁 중개사가 주소 확인 → 매도자에게 직접 연락",
                  "수수료 없이 매물 가로채기 — 노력이 사라짐",
                  "다음부터는 좋은 매물을 숨김 → 시장 신뢰 붕괴",
                ].map((text, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{
                      flexShrink: 0, width: 22, height: 22, borderRadius: "50%",
                      background: "rgba(239,68,68,0.12)", color: "#F87171",
                      fontSize: 10, fontWeight: 800,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>{i + 1}</div>
                    <p style={{ margin: 0, fontSize: 15, color: "#CBD5E1", lineHeight: 1.65 }}>{text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 땅신뢰 */}
            <div style={{
              background: "rgba(212,168,83,0.04)",
              border: "1px solid rgba(212,168,83,0.2)",
              borderRadius: 20, padding: m ? "26px 22px" : "36px 32px",
              position: "relative" as const,
            }}>
              <div style={{
                position: "absolute" as const, top: 0, left: 0, right: 0, height: 3,
                borderRadius: "20px 20px 0 0",
                background: "linear-gradient(90deg, #D4A853, #E8C77A)",
              }} />
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "4px 12px", borderRadius: 100,
                background: "rgba(212,168,83,0.1)", color: "#D4A853",
                fontSize: 10, fontWeight: 700, letterSpacing: 2,
                textTransform: "uppercase" as const, marginBottom: 22,
              }}>
                {"✔️"} 다가치 땅신뢰
              </span>
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
                {[
                  "매물 등록 시 시스템이 자동으로 주소를 분리 보관",
                  "읍면동 수준 + 대략 좌표만 공개 (반경 ~1.5km)",
                  "관심 상대가 NDA(비밀유지계약) 서명",
                  "즉시 정확한 지번 + 소유주 연락처 공개 → 안전 공동중개",
                ].map((text, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{
                      flexShrink: 0, width: 22, height: 22, borderRadius: "50%",
                      background: "rgba(212,168,83,0.12)", color: "#D4A853",
                      fontSize: 10, fontWeight: 800,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>{i + 1}</div>
                    <p style={{ margin: 0, fontSize: 15, color: "#CBD5E1", lineHeight: 1.65 }}>{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          매물 미리보기
      ══════════════════════════════════════ */}
      {previews.length > 0 && (
        <section
          ref={listingsSec.ref}
          style={{
            padding: m ? "64px 20px" : "80px 24px",
            background: "#07101E",
            ...fadeIn(previewVisible),
          }}
        >
          <div style={{ maxWidth: 980, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: m ? 36 : 48 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 2.5,
                textTransform: "uppercase" as const, color: "#D4A853", display: "block", marginBottom: 12,
              }}>LIVE LISTINGS</span>
              <h2 style={{
                fontSize: m ? 22 : 32, fontWeight: 800,
                color: "#F8FAFC", margin: "0 0 14px", letterSpacing: "-0.02em",
              }}>현재 등록된 매물</h2>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                padding: "6px 16px", borderRadius: 100,
                background: "rgba(212,168,83,0.07)",
                border: "1px solid rgba(212,168,83,0.18)",
                color: "#94A3B8", fontSize: 13,
              }}>
                <span style={{ color: "#D4A853" }}>{"\uD83D\uDD12"}</span>
                정확한 위치는 NDA 서명 후 공개됩니다
              </div>
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: m ? "1fr" : "repeat(3, 1fr)",
              gap: m ? 12 : 18,
            }}>
              {previews.map(l => (
                <div
                  key={l.id}
                  className="lt-preview"
                  style={{
                    background: "rgba(255,255,255,0.028)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    borderRadius: 14, padding: m ? "16px 14px" : "20px 18px",
                    cursor: "pointer",
                    borderLeft: `3px solid ${TYPE_COLORS[l.property_type] || "#475569"}`,
                    transition: "all 0.22s",
                  }}
                  onClick={() => navigate(`/listings/${l.id}`)}
                  role="button"
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{
                      padding: "2px 9px", borderRadius: 7,
                      background: "rgba(212,168,83,0.1)",
                      color: "#D4A853", fontSize: 11, fontWeight: 700,
                    }}>{l.property_type}</span>
                    <span style={{ fontSize: 11, color: "#64748B" }}>
                      {l.region_sido} {l.region_sigungu}
                    </span>
                  </div>
                  <div style={{ fontSize: m ? 20 : 22, fontWeight: 800, color: "#FBBF24", marginBottom: 4 }}>
                    {formatPrice(l.price_manwon)}
                    <span style={{ fontSize: 12, color: "#64748B", fontWeight: 500, marginLeft: 2 }}>원</span>
                  </div>
                  <div style={{ fontSize: 13, color: "#94A3B8", lineHeight: 1.5 }}>
                    {l.area_pyeong.toLocaleString()}평
                    {l.land_category ? ` · ${l.land_category}` : ""}
                    {l.area_pyeong > 0 ? ` · 평당 ${Math.round(l.price_manwon / l.area_pyeong).toLocaleString()}만` : ""}
                  </div>
                  {l.broker_is_verified && (
                    <div style={{ marginTop: 6, fontSize: 10, color: "#22C55E", fontWeight: 600 }}>
                      {"\u2713"} 인증 중개사
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ textAlign: "center", marginTop: m ? 28 : 40 }}>
              <Link to="/listings" style={{
                color: "#D4A853", fontSize: 15, fontWeight: 700,
                textDecoration: "none",
                display: "inline-flex", alignItems: "center", gap: 5,
              }}>
                전체 매물 보기 {"\u2192"}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════
          숫자로 보는 땅신뢰
      ══════════════════════════════════════ */}
      <section
        ref={statsSec.ref}
        style={{
          padding: m ? "64px 20px" : "80px 24px",
          background: "linear-gradient(160deg, #0A1020 0%, #0D1828 100%)",
          ...fadeIn(statsSec.visible),
        }}
      >
        <div style={{ maxWidth: 820, margin: "0 auto", textAlign: "center" }}>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 2.5,
            textTransform: "uppercase" as const, color: "#D4A853", display: "block", marginBottom: 12,
          }}>BY THE NUMBERS</span>
          <h2 style={{
            fontSize: m ? 22 : 32, fontWeight: 800,
            color: "#F8FAFC", margin: "0 0 52px", letterSpacing: "-0.02em",
          }}>숫자로 보는 다가치 땅신뢰</h2>

          <div style={{
            display: "grid",
            gridTemplateColumns: m ? "1fr 1fr" : "repeat(4, 1fr)",
            gap: m ? "32px 16px" : 0,
          }}>
            {[
              { count: cntListings,  suffix: "+", label: "등록 매물",  desc: "농지·임야·공장·창고·묘터" },
              { count: cntBrokers,   suffix: "+", label: "가입 중개사", desc: "공인중개사 자격증 보유" },
              { count: cntInquiries, suffix: "+", label: "매물 문의",  desc: "등록 매물 문의 건수" },
              { count: cntNda,       suffix: "+", label: "NDA 체결",   desc: "비공개 정보 열람" },
            ].map((s, i) => (
              <div key={s.label} style={{
                padding: m ? 0 : "0 24px",
                borderRight: (!m && i < 3) ? "1px solid rgba(255,255,255,0.05)" : "none",
              }}>
                <div style={{
                  fontSize: m ? 44 : 54, fontWeight: 800, lineHeight: 1,
                  background: "linear-gradient(135deg, #C49A3C 0%, #D4A853 50%, #E8C77A 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  marginBottom: 8,
                }}>
                  {s.count.toLocaleString()}{s.suffix}
                </div>
                <div style={{ fontSize: m ? 14 : 16, fontWeight: 700, color: "#E2E8F0", marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 12, color: "#94A3B8" }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          로드맵 섹션 (곧 만나실 기능들)
      ══════════════════════════════════════ */}
      <section
        ref={roadmapSec.ref}
        style={{
          padding: m ? "64px 20px" : "80px 24px",
          background: "var(--bg-primary)",
          ...fadeIn(roadmapSec.visible),
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: m ? 36 : 52 }}>
            <h2 style={{
              fontSize: m ? 22 : 32, fontWeight: 800,
              color: "#F8FAFC", margin: "0 0 10px", letterSpacing: "-0.02em",
            }}>핵심 기능 모두 오픈</h2>
            <p style={{ fontSize: m ? 14 : 16, color: "#64748B", margin: 0 }}>
              다가치 땅신뢰의 핵심 기능
            </p>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: m ? "1fr 1fr" : "repeat(4, 1fr)",
            gap: m ? 12 : 16,
          }}>
            {ROADMAP_ITEMS.map(item => (
              <div key={item.title} className="lt-roadmap-card" style={{
                background: "var(--bg-card)",
                border: `1px solid ${item.open ? "rgba(16,185,129,0.25)" : "var(--border-light)"}`,
                borderRadius: 16,
                padding: m ? "24px 16px" : "32px 24px",
                textAlign: "center",
                position: "relative" as const,
              }}>
                <div style={{
                  position: "absolute" as const, top: 12, right: 12,
                  fontSize: 9, padding: "2px 8px", borderRadius: 100,
                  background: item.open ? "rgba(16,185,129,0.15)" : "rgba(100,116,139,0.12)",
                  color: item.open ? "#10B981" : "#64748B",
                  fontWeight: 700, letterSpacing: 1.2,
                  textTransform: "uppercase" as const,
                }}>{item.open ? "\u2705 \uC624\uD508" : "\uC900\uBE44 \uC911"}</div>
                <div style={{ fontSize: m ? 36 : 44, marginBottom: 12, lineHeight: 1 }}>{item.icon}</div>
                <p style={{
                  fontSize: m ? 13 : 14, fontWeight: 600,
                  color: "#94A3B8", margin: 0, lineHeight: 1.5,
                }}>{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          블록체인 신뢰 배지
      ══════════════════════════════════════ */}
      <section style={{
        padding: m ? "56px 20px" : "72px 24px",
        background: "linear-gradient(180deg, var(--bg-primary) 0%, #0A0F1E 100%)",
        textAlign: "center",
      }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(130,71,229,0.08)", border: "1px solid rgba(130,71,229,0.2)",
            borderRadius: 100, padding: "5px 16px", marginBottom: 20,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#8247E5", display: "inline-block" }} />
            <span style={{ color: "#8247E5", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em" }}>POLYGON BLOCKCHAIN</span>
          </div>
          <h3 style={{
            fontSize: m ? 20 : 26, fontWeight: 800,
            color: "#F8FAFC", margin: "0 0 10px",
          }}>
            중개사 인증, 블록체인으로 영구 보증
          </h3>
          <p style={{
            fontSize: m ? 13 : 15, color: "#64748B",
            lineHeight: 1.8, margin: "0 auto 20px", maxWidth: 460,
          }}>
            인증된 중개사에게 Polygon 블록체인 기반 신뢰 배지(SBT)를 발행합니다.
            위변조 불가, 영구 보존. 거래 상대방이 직접 검증할 수 있습니다.
          </p>
          <a
            href="https://polygonscan.com/address/0x2DC61b0E0D2d1069CDEA86a0F18eEaC8748Ab5a2"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "monospace", fontSize: 11,
              color: "rgba(130,71,229,0.6)", textDecoration: "none",
            }}
          >
            BrokerBadge Contract: 0x2DC6...Ab5a2
          </a>
        </div>
      </section>

      {/* ══════════════════════════════════════
          하단 CTA
      ══════════════════════════════════════ */}
      <section
        ref={ctaSec.ref}
        style={{
          position: "relative" as const,
          overflow: "hidden" as const,
          padding: m ? "80px 20px" : "120px 24px",
          background: "#040C18",
          textAlign: "center",
          ...fadeIn(ctaSec.visible),
        }}
      >
        <div style={{
          position: "absolute" as const, top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: m ? 280 : 640, height: m ? 280 : 480,
          borderRadius: "50%",
          background: "radial-gradient(ellipse, rgba(212,168,83,0.08) 0%, transparent 65%)",
          pointerEvents: "none" as const,
        }} />

        <div style={{ position: "relative" as const, zIndex: 1, maxWidth: 560, margin: "0 auto" }}>
          <div style={{
            width: 44, height: 44, margin: "0 auto 24px",
            background: "linear-gradient(135deg, #D4A853, #C49A3C)",
            transform: "rotate(45deg)",
            borderRadius: 7,
            boxShadow: "0 0 28px rgba(212,168,83,0.3)",
          }} />
          <h2 style={{
            fontSize: m ? 28 : 46, fontWeight: 800,
            color: "#F8FAFC", margin: "0 0 14px",
            lineHeight: 1.2, letterSpacing: "-0.025em",
          }}>지금 시작하세요</h2>
          <p style={{
            fontSize: m ? 15 : 17, color: "#94A3B8",
            lineHeight: 1.8, margin: "0 auto 44px", maxWidth: 400,
          }}>
            인증된 중개사만 참여하는 프라이빗 토지 거래 네트워크.<br />
            좋은 매물은 여기 숨겨져 있습니다.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" as const }}>
            <button
              className="lt-btn-gold"
              style={{
                padding: m ? "15px 36px" : "18px 48px",
                borderRadius: 12, border: "none",
                background: "linear-gradient(135deg, #D4A853 0%, #C49A3C 100%)",
                color: "#080E1A", fontSize: m ? 15 : 17, fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 4px 24px rgba(212,168,83,0.32)",
              }}
              onClick={() => navigate("/signup")}
            >중개사 가입하기</button>
            <button
              className="lt-btn-ghost"
              style={{
                padding: m ? "15px 36px" : "18px 48px",
                borderRadius: 12,
                border: "1.5px solid rgba(148,163,184,0.22)",
                background: "transparent",
                color: "#94A3B8", fontSize: m ? 15 : 17, fontWeight: 600,
                cursor: "pointer", transition: "all 0.22s",
              }}
              onClick={() => navigate("/listings")}
            >매물 먼저 보기</button>
          </div>

          {/* 연락처 */}
          <div style={{
            marginTop: m ? 28 : 36,
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: m ? 10 : 16, flexWrap: "wrap" as const,
          }}>
            <a href="tel:07082258585" style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: m ? 13 : 14, color: "#64748B", textDecoration: "none",
              border: "1px solid rgba(100,116,139,0.2)",
              padding: "7px 16px", borderRadius: 100,
              transition: "all 0.2s",
            }}>
              {"\uD83D\uDCDE"} 070-8225-8585
            </a>
            <a href="mailto:admin@genomic.cc" style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: m ? 13 : 14, color: "#64748B", textDecoration: "none",
              border: "1px solid rgba(100,116,139,0.2)",
              padding: "7px 16px", borderRadius: 100,
              transition: "all 0.2s",
            }}>
              {"\u2709\uFE0F"} admin@genomic.cc
            </a>
          </div>
        </div>
      </section>

      <AppFooter />

      {/* ══════════════════════════════════════
          땅주인 준비 중 모달
      ══════════════════════════════════════ */}
      {showLandModal && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.75)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
          }}
          onClick={() => setShowLandModal(false)}
        >
          <div
            style={{
              background: "#0D1828",
              border: "1px solid rgba(212,168,83,0.3)",
              borderTop: "2px solid #D4A853",
              borderRadius: 20,
              padding: m ? "32px 24px" : "48px 52px",
              maxWidth: 460, width: "100%",
              textAlign: "center",
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 52, marginBottom: 16, lineHeight: 1 }}>{"\uD83C\uDF31"}</div>
            <h3 style={{
              fontSize: m ? 20 : 24, fontWeight: 800,
              color: "#F8FAFC", margin: "0 0 12px", letterSpacing: "-0.02em",
            }}>곧 오픈 예정입니다</h3>
            <p style={{
              fontSize: m ? 14 : 16, color: "#94A3B8",
              lineHeight: 1.75, margin: "0 0 28px",
            }}>
              땅주인 직접 등록 기능을 준비 중입니다.<br />
              오픈 시 가장 먼저 알려드릴까요?
            </p>
            {landAlertSent ? (
              <div style={{
                padding: "14px 24px", borderRadius: 10,
                background: "rgba(34,197,94,0.08)",
                border: "1px solid rgba(34,197,94,0.2)",
                color: "#22C55E", fontSize: 15, fontWeight: 600,
              }}>
                {"\u2705"} 알림 신청이 완료되었습니다!
              </div>
            ) : (
              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                <button
                  className="lt-btn-gold"
                  style={{
                    padding: "13px 28px", borderRadius: 10, border: "none",
                    background: "linear-gradient(135deg, #D4A853, #C49A3C)",
                    color: "#080E1A", fontSize: m ? 14 : 15, fontWeight: 700,
                    cursor: "pointer",
                  }}
                  onClick={() => setLandAlertSent(true)}
                >
                  알림 신청하기
                </button>
                <button
                  onClick={() => setShowLandModal(false)}
                  style={{
                    padding: "13px 28px", borderRadius: 10,
                    border: "1.5px solid rgba(148,163,184,0.22)",
                    background: "transparent",
                    color: "#94A3B8", fontSize: m ? 14 : 15,
                    cursor: "pointer",
                  }}
                >
                  닫기
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
